/**
 * Pencere (modal) — **uzuv** mertebesi, sayfanın ÜSTÜNDE.
 *
 * Neden var: rol düzenleme gibi işler sayfanın içine gömülünce ekran iç içe
 * görünüyor ve kullanıcı "neredeyim" sorusunu soruyor. Bu tür bir iş sayfayı
 * itmemeli — üstünde açılıp kapanmalı.
 *
 * Erişilebilirlik ve davranış kararları:
 *   - `role="dialog"` + `aria-modal` + başlığa `aria-labelledby`
 *   - **Esc kapatır** ve arka plana tıklamak kapatır
 *   - Açıkken gövde kaydırması kilitlenir (arka plan kaymasın)
 *   - Açılınca odak pencereye taşınır; kapanınca **çağıran düğmeye döner**
 *     (klavye kullanıcısı listenin başına fırlamasın)
 *   - Sekme tuşu pencerenin İÇİNDE döner (odak tuzağı) — aksi hâlde kullanıcı
 *     görünmeyen arka plan düğmelerine geçer
 *
 * Telefonda pencere tam genişliğe yayılır ve alttan yapışır; masaüstünde
 * ortalanır. İçerik uzunsa pencere içinde kayar, sayfa kaymaz.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { Dugme } from './Dugme';
import { cn } from '../../lib/cn';

/** Odaklanabilir öğeler — odak tuzağı için. */
const ODAKLANABILIR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface PencereProps {
  acik: boolean;
  baslik: string;
  aciklama?: string;
  /** Başlığın yanındaki ek bilgi (rozet vb.). */
  basliksag?: ReactNode;
  /** Alt çubuk — kaydet/vazgeç düğmeleri. */
  altCubuk?: ReactNode;
  onKapat: () => void;
  children: ReactNode;
  /** Geniş içerik (izin matrisi gibi) için ferah pencere. */
  genis?: boolean;
}

export function Pencere({
  acik,
  baslik,
  aciklama,
  basliksag,
  altCubuk,
  onKapat,
  children,
  genis = false,
}: PencereProps) {
  const pencereRef = useRef<HTMLDivElement>(null);
  const oncekiOdak = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!acik) return;

    // Açılmadan önceki odağı sakla — kapanınca oraya dönülecek.
    oncekiOdak.current = document.activeElement as HTMLElement | null;

    const oncekiTasma = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Odağı pencereye taşı (ilk odaklanabilir öğe, yoksa pencerenin kendisi).
    const ilk = pencereRef.current?.querySelector<HTMLElement>(ODAKLANABILIR);
    (ilk ?? pencereRef.current)?.focus();

    const tusDinleyici = (olay: KeyboardEvent): void => {
      if (olay.key === 'Escape') {
        olay.preventDefault();
        onKapat();
        return;
      }
      if (olay.key !== 'Tab') return;

      // Odak tuzağı: sekme pencerenin içinde dönsün.
      const odaklar = pencereRef.current?.querySelectorAll<HTMLElement>(ODAKLANABILIR);
      if (!odaklar || odaklar.length === 0) return;
      const ilkOdak = odaklar[0];
      const sonOdak = odaklar[odaklar.length - 1];
      if (!ilkOdak || !sonOdak) return;

      if (olay.shiftKey && document.activeElement === ilkOdak) {
        olay.preventDefault();
        sonOdak.focus();
      } else if (!olay.shiftKey && document.activeElement === sonOdak) {
        olay.preventDefault();
        ilkOdak.focus();
      }
    };

    window.addEventListener('keydown', tusDinleyici);

    return () => {
      document.body.style.overflow = oncekiTasma;
      window.removeEventListener('keydown', tusDinleyici);
      // Odağı çağıran düğmeye geri ver.
      oncekiOdak.current?.focus();
    };
  }, [acik, onKapat]);

  if (!acik) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      {/* Arka plan — tıklayınca kapanır. Klavye için de düğme. */}
      <button
        type="button"
        aria-label="Pencereyi kapat"
        onClick={onKapat}
        className="absolute inset-0 bg-[color-mix(in_srgb,var(--ink)_45%,transparent)]"
      />

      <div
        ref={pencereRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pencere-baslik"
        tabIndex={-1}
        className={cn(
          'kap relative flex max-h-[92dvh] w-full flex-col',
          'border border-[var(--line)] bg-[var(--surface)] shadow-[var(--shadow-float)]',
          // Telefonda alta yapışır (üst köşeler yuvarlak), masaüstünde ortalanır.
          'rounded-t-[var(--radius-lg)] sm:rounded-[var(--radius-lg)]',
          genis ? 'sm:max-w-4xl' : 'sm:max-w-2xl'
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-[var(--line)] p-4">
          <div className="flex min-w-0 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="pencere-baslik" className="text-lg font-semibold text-[var(--ink)]">
                {baslik}
              </h2>
              {basliksag}
            </div>
            {aciklama && <p className="text-sm text-[var(--ink-muted)]">{aciklama}</p>}
          </div>
          <Dugme gorunum="sessiz" boy="sm" aria-label="Kapat" ikon={<X size={18} />} onClick={onKapat} />
        </header>

        {/* İçerik pencerenin içinde kayar — sayfa kaymaz. */}
        <div className="flex-1 overflow-y-auto p-4">{children}</div>

        {altCubuk && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] bg-[var(--surface-sunken)] p-4">
            {altCubuk}
          </footer>
        )}
      </div>
    </div>
  );
}
